import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { StockDiscrepancyService } from '../service/stock-discrepancy.service';
import { StockDiscrepancy, StockDiscrepancyCreateRequest, StockDiscrepancyAutoRequest, StockDiscrepancyStats } from '../Module/StockDiscrepancy.model';
import { ProductService } from '../service/product.service';
import { Product } from '../Module/Product.model';
import { AuthService } from '../service/auth.service';
import { NotificationService } from '../service/notification.service';
import { NotificationRefreshService } from '../service/notification-refresh.service';

@Component({
  selector: 'app-stock-discrepancy',
  templateUrl: './stock-discrepancy.component.html',
  styleUrls: ['./stock-discrepancy.component.css']
})
export class StockDiscrepancyComponent implements OnInit {
  
  discrepancies: StockDiscrepancy[] = [];
  pendingDiscrepancies: StockDiscrepancy[] = [];
  approvedNotAppliedDiscrepancies: StockDiscrepancy[] = [];
  products: Product[] = [];
  filteredProducts: Product[] = [];
  stats: StockDiscrepancyStats | null = null;
  isLoading = false;
  isAdmin = false;
  
  // Formulaire de création
  showCreateForm = false;
  showAutoForm = false;
  createForm: StockDiscrepancyCreateRequest = {
    productId: 0,
    realQuantity: 0,
    systemQuantity: 0,
    comment: ''
  };
  
  autoForm: StockDiscrepancyAutoRequest = {
    productId: 0,
    realQuantity: 0
  };

  // Properties for automatic form display
  autoSystemQuantity: number = 0;
  autoCalculatedDiscrepancy: number = 0;

  // Search functionality
  productSearchTerm: string = '';
  showProductDropdown: boolean = false;

  // Confirmation dialog properties
  showDeleteConfirmation = false;
  discrepancyToDelete: number | null = null;

  // Edit properties
  showEditForm = false;
  editingDiscrepancyId: number | null = null;
  editForm = {
    realQuantity: 0,
    systemQuantity: 0,
    comment: ''
  };
  
  // Filtres
  statusFilter = 'all';
  productFilter = 0;
  filterDate: string = '';
  filterProductSearchTerm: string = '';
  showFilterProductDropdown: boolean = false;
  filteredFilterProducts: Product[] = [];
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 0;
  pages: number[] = [];
  pagedItemsSize = 0;
  Math = Math; // Exposer Math pour le template
  Array = Array; // Exposer Array pour le template

  constructor(
    private stockDiscrepancyService: StockDiscrepancyService,
    private productService: ProductService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private notificationRefreshService: NotificationRefreshService
  ) { }

  ngOnInit(): void {
    this.checkUserRole();
    this.loadDiscrepancies();
    this.refreshProducts();
  }

  private refreshProducts(): void {
    // Reload products from backend to ensure quantities reflect latest transactions
    this.productService.listProducts().subscribe({
      next: (data: Product[]) => {
        this.products = [...data];
        this.filteredProducts = data.slice(0, 10);
      },
      error: (error: any) => {
        console.error('Erreur lors du chargement des produits:', error);
      }
    });
  }

  checkUserRole(): void {
    const user = this.authService.getUser();
    this.isAdmin = user?.role === 'ADMIN';
  }

  loadDiscrepancies(): void {
    this.isLoading = true;
    this.stockDiscrepancyService.getPaginatedStockDiscrepancies(
      this.currentPage - 1,
      this.itemsPerPage,
      this.statusFilter,
      this.productFilter,
      this.filterDate
    ).subscribe({
      next: (data) => {
        this.discrepancies = data.content;
        this.totalItems = data.totalElements;
        this.totalPages = data.totalPages;
        this.pagedItemsSize = data.content.length;
        this.pages = Array.from({ length: data.totalPages }, (_, i) => i + 1);
        this.stats = this.stockDiscrepancyService.calculateStats(data.content);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [STOCK-DISCREPANCY] Erreur lors du chargement des écarts:', error);
        this.notificationService.error('Erreur lors du chargement des écarts de stock');
        this.isLoading = false;
      }
    });
  }

  loadPendingDiscrepancies(): void {
    if (this.isAdmin) {
      this.stockDiscrepancyService.getPendingStockDiscrepancies().subscribe({
              next: (data) => {
        this.pendingDiscrepancies = data;
      },
        error: (error) => {
          console.error('❌ [STOCK-DISCREPANCY] Erreur lors du chargement des écarts en attente:', error);
        }
      });
    }
  }

  loadApprovedNotAppliedDiscrepancies(): void {
    if (this.isAdmin) {
      this.stockDiscrepancyService.getApprovedNotAppliedStockDiscrepancies().subscribe({
              next: (data) => {
        this.approvedNotAppliedDiscrepancies = data;
      },
        error: (error) => {
          console.error('❌ [STOCK-DISCREPANCY] Erreur lors du chargement des écarts approuvés non appliqués:', error);
        }
      });
    }
  }

  toggleCreateForm(): void {
    this.showCreateForm = !this.showCreateForm;
    this.showAutoForm = false;
    if (this.showCreateForm) {
      this.resetCreateForm();
    }
  }

  toggleAutoForm(): void {
    this.showAutoForm = !this.showAutoForm;
    this.showCreateForm = false;
    if (this.showAutoForm) {
      this.resetAutoForm();
    }
  }

  resetCreateForm(): void {
    this.createForm = {
      productId: 0,
      realQuantity: 0,
      systemQuantity: 0,
      comment: ''
    };
  }

  resetAutoForm(): void {
    this.autoForm = {
      productId: 0,
      realQuantity: 0
    };
    this.productSearchTerm = '';
    this.showProductDropdown = false;
    this.autoSystemQuantity = 0;
    this.autoCalculatedDiscrepancy = 0;
  }

  onProductChange(): void {
    if (this.createForm.productId) {
      const selectedProduct = this.products.find(p => p.id === this.createForm.productId);
      if (selectedProduct && selectedProduct.quantity !== undefined) {
        this.createForm.systemQuantity = selectedProduct.quantity;
      }
    }
  }

  calculateDiscrepancy(): number {
    return this.createForm.realQuantity - this.createForm.systemQuantity;
  }

  createDiscrepancy(): void {
    if (!this.validateCreateForm()) {
      return;
    }

    this.isLoading = true;
    this.stockDiscrepancyService.createStockDiscrepancy(this.createForm).subscribe({
      next: (data) => {
        this.notificationService.success('Écart de stock créé avec succès');
        this.showCreateForm = false;
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        this.loadApprovedNotAppliedDiscrepancies();
        this.refreshProducts();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [STOCK-DISCREPANCY] Erreur lors de la création de l\'écart:', error);
        this.notificationService.error('Erreur lors de la création de l\'écart de stock');
        this.isLoading = false;
      }
    });
  }

  createAutomaticDiscrepancy(): void {
    console.log('�� [AUTO-DISCREPANCY] Creating automatic discrepancy with productId:', this.autoForm.productId);
    
    if (!this.validateAutoForm()) {
      console.log('❌ [AUTO-DISCREPANCY] Validation failed');
      this.notificationService.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    
    console.log('✅ [AUTO-DISCREPANCY] Validation successful');
    this.isLoading = true;
    
    const request = {
      productId: this.autoForm.productId,
      realQuantity: this.autoForm.realQuantity,
      comment: this.autoForm.comment
    };
    
    this.stockDiscrepancyService.createAutomaticStockDiscrepancy(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.showAutoForm = false;
        this.loadDiscrepancies();
        this.refreshProducts();
        this.notificationService.success('Écart automatique créé avec succès.');
      },
      error: (error) => {
        console.error('❌ [AUTO-DISCREPANCY] Error creating automatic discrepancy:', error);
        this.isLoading = false;
        this.notificationService.error('Erreur lors de la création de l\'écart automatique.');
      }
    });
  }

  validateCreateForm(): boolean {
    if (this.createForm.productId === 0) {
      this.notificationService.error('Veuillez sélectionner un produit');
      return false;
    }
    if (this.createForm.realQuantity < 0) {
      this.notificationService.error('La quantité réelle ne peut pas être négative');
      return false;
    }
    if (this.createForm.systemQuantity < 0) {
      this.notificationService.error('La quantité système ne peut pas être négative');
      return false;
    }
    return true;
  }

  validateAutoForm(): boolean {
    if (this.autoForm.productId === 0) {
      this.notificationService.error('Veuillez sélectionner un produit');
      return false;
    }
    if (this.autoForm.realQuantity < 0) {
      this.notificationService.error('La quantité réelle ne peut pas être négative');
      return false;
    }
    return true;
  }

  approveDiscrepancy(id: number): void {
    this.isLoading = true;
    this.stockDiscrepancyService.approveStockDiscrepancy(id).subscribe({
      next: (data) => {
        this.notificationService.success('Écart approuvé avec succès');
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        this.loadApprovedNotAppliedDiscrepancies();
        this.refreshProducts();
        
        // Refresh notifications in nav-bar
        this.refreshNavBarNotifications();
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [STOCK-DISCREPANCY] Erreur lors de l\'approbation:', error);
        this.notificationService.error('Erreur lors de l\'approbation de l\'écart');
        this.isLoading = false;
      }
    });
  }

  rejectDiscrepancy(id: number): void {
    this.isLoading = true;
    this.stockDiscrepancyService.rejectStockDiscrepancy(id).subscribe({
      next: (data) => {
        this.notificationService.success('Écart rejeté avec succès');
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        this.loadApprovedNotAppliedDiscrepancies();
        this.refreshProducts();
        
        // Refresh notifications in nav-bar
        this.refreshNavBarNotifications();
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [STOCK-DISCREPANCY] Erreur lors du rejet:', error);
        this.notificationService.error('Erreur lors du rejet de l\'écart');
        this.isLoading = false;
      }
    });
  }

  deleteDiscrepancy(id: number): void {
    this.discrepancyToDelete = id;
    this.showDeleteConfirmation = true;
  }

  confirmDelete(): void {
    if (this.discrepancyToDelete) {
      this.isLoading = true;
      this.stockDiscrepancyService.deleteStockDiscrepancy(this.discrepancyToDelete).subscribe({
        next: () => {
          this.notificationService.success('Écart supprimé avec succès');
          this.loadDiscrepancies();
          this.loadPendingDiscrepancies();
          this.loadApprovedNotAppliedDiscrepancies();
          this.refreshProducts();
          
          // Refresh notifications in nav-bar
          this.refreshNavBarNotifications();
          
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          this.discrepancyToDelete = null;
        },
        error: (error) => {
          console.error('❌ [STOCK-DISCREPANCY] Erreur lors de la suppression:', error);
          this.notificationService.error('Erreur lors de la suppression de l\'écart');
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          this.discrepancyToDelete = null;
        }
      });
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.discrepancyToDelete = null;
  }

  // Edit methods
  openEditForm(discrepancy: StockDiscrepancy): void {
    this.editingDiscrepancyId = discrepancy.id || null;
    this.editForm = {
      realQuantity: discrepancy.realQuantity,
      systemQuantity: discrepancy.systemQuantity,
      comment: discrepancy.comment || ''
    };
    this.showEditForm = true;
  }

  closeEditForm(): void {
    this.showEditForm = false;
    this.editingDiscrepancyId = null;
    this.editForm = { realQuantity: 0, systemQuantity: 0, comment: '' };
  }

  saveEditForm(): void {
    if (!this.editingDiscrepancyId) {
      this.notificationService.error('ID d\'écart invalide');
      return;
    }

    this.isLoading = true;
    this.stockDiscrepancyService.updateStockDiscrepancy(
      this.editingDiscrepancyId,
      this.editForm.realQuantity,
      this.editForm.systemQuantity,
      this.editForm.comment
    ).subscribe({
      next: (data) => {
        this.notificationService.success('Écart mis à jour avec succès');
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        this.loadApprovedNotAppliedDiscrepancies();
        this.refreshProducts();
        this.closeEditForm();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [STOCK-DISCREPANCY] Erreur lors de la mise à jour:', error);
        this.notificationService.error('Erreur lors de la mise à jour de l\'écart');
        this.isLoading = false;
      }
    });
  }

  calculateEditDiscrepancy(): number {
    return this.editForm.realQuantity - this.editForm.systemQuantity;
  }

  getFilteredDiscrepancies(): StockDiscrepancy[] {
    let filtered = this.discrepancies;

    // Filtre par statut
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === this.statusFilter);
    }

    // Filtre par produit
    if (this.productFilter !== 0) {
      filtered = filtered.filter(d => d.productId === this.productFilter);
    }

    return filtered;
  }

  getPaginatedDiscrepancies(): StockDiscrepancy[] {
    // Return current page data (already paginated from backend)
    return this.discrepancies;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadDiscrepancies();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadDiscrepancies();
  }

  resetFilters(): void {
    this.statusFilter = 'all';
    this.productFilter = 0;
    this.filterProductSearchTerm = '';
    this.filterDate = '';
    this.showFilterProductDropdown = false;
    this.currentPage = 1;
    this.loadDiscrepancies();
  }

  onFilterProductSearchChange(): void {
    this.filterFilterProducts();
  }

  onFilterProductSearchFocus(): void {
    this.showFilterProductDropdown = true;
    this.filterFilterProducts();
  }

  filterFilterProducts(): void {
    if (!this.filterProductSearchTerm.trim()) {
      this.filteredFilterProducts = this.products.slice(0, 10);
      return;
    }

    const normalizedSearch = this.normalize(this.filterProductSearchTerm);
    const searchTokens = normalizedSearch.split(' ');

    this.filteredFilterProducts = this.products
      .filter(product => {
        const combined = this.normalize(
          `${product.name ?? ''} ${product.codeBare ?? ''} ${product.description ?? ''}`
        );
        return searchTokens.every(token => combined.includes(token));
      })
      .slice(0, 10);
  }

  selectFilterProduct(product: Product): void {
    this.productFilter = product.id ?? 0;
    this.filterProductSearchTerm = product.name ?? '';
    this.showFilterProductDropdown = false;
    this.applyFilters();
  }

  hideFilterProductDropdown(): void {
    setTimeout(() => {
      this.showFilterProductDropdown = false;
    }, 200);
  }

  getTotalPages(): number {
    return this.totalPages;
  }

  getProductName(productId: number): string {
    const product = this.products.find(p => p.id === productId);
    return product && product.name ? product.name : 'Produit inconnu';
  }

  // Méthodes utilitaires pour l'affichage
  formatDate(dateString: string): string {
    return this.stockDiscrepancyService.formatDate(dateString);
  }

  formatQuantity(quantity: number): string {
    return this.stockDiscrepancyService.formatQuantity(quantity);
  }

  getStatusClass(status: string): string {
    return this.stockDiscrepancyService.getStatusClass(status);
  }

  getDiscrepancyClass(quantity: number): string {
    return this.stockDiscrepancyService.getDiscrepancyClass(quantity);
  }

  getAppliedClass(applied: boolean): string {
    return this.stockDiscrepancyService.getAppliedClass(applied);
  }

  // Search functionality for products
  normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  onProductSearchChange(): void {
    this.filterProducts();
  }

  onProductSearchFocus(): void {
    this.showProductDropdown = true;
    this.filterProducts();
  }

  filterProducts(): void {
    if (!this.productSearchTerm.trim()) {
      this.filteredProducts = this.products.slice(0, 10);
      return;
    }

    const normalizedSearch = this.normalize(this.productSearchTerm);
    const searchTokens = normalizedSearch.split(' ');

    this.filteredProducts = this.products
      .filter(product => {
        const combined = this.normalize(
          `${product.name ?? ''} ${product.codeBare ?? ''} ${product.description ?? ''}`
        );
        return searchTokens.every(token => combined.includes(token));
      })
      .slice(0, 10);
  }

  selectProduct(product: Product): void {
    this.autoForm.productId = product.id ?? 0;
    this.productSearchTerm = product.name ?? '';
    this.showProductDropdown = false;
    
    // Update the display properties
    this.autoSystemQuantity = product.quantity ?? 0;
    this.autoCalculatedDiscrepancy = this.autoForm.realQuantity - this.autoSystemQuantity;
    
    // Force change detection to update the template
    this.cdr.detectChanges();
  }

  hideProductDropdown(): void {
    setTimeout(() => {
      this.showProductDropdown = false;
    }, 200);
  }

  // Helpers pour le formulaire automatique
  getAutoSystemQuantity(): number {
    const selectedProduct = this.products.find(p => p.id === this.autoForm.productId);
    return selectedProduct && selectedProduct.quantity !== undefined ? selectedProduct.quantity : 0;
  }

  calculateAutoDiscrepancy(): number {
    const selectedProduct = this.products.find(p => p.id === this.autoForm.productId);
    if (selectedProduct && this.autoForm.realQuantity !== null && selectedProduct.quantity !== undefined) {
      return this.autoForm.realQuantity - selectedProduct.quantity;
    }
    return 0;
  }

  onAutoProductChange(): void {
    // Auto-populate system quantity when product is selected
    if (this.autoForm.productId) {
      const selectedProduct = this.products.find(p => p.id === this.autoForm.productId);
      if (selectedProduct) {
        this.productSearchTerm = selectedProduct.name ?? '';
      }
    }
  }

  onAutoRealQuantityChange(): void {
    // Recalculate the discrepancy when real quantity changes
    this.autoCalculatedDiscrepancy = this.autoForm.realQuantity - this.autoSystemQuantity;
  }

  private refreshNavBarNotifications(): void {
    // Use the notification refresh service
    this.notificationRefreshService.refreshNotifications();
  }
} 